
import { supabase } from "@/integrations/supabase/client";
import { Flight } from "@/types/flight";

interface UnifiedFlightRow {
  id: string;
  flight_number: string;
  scheduled_departure: string;
  scheduled_arrival: string;
  status: string;
  status_reason: string | null;
  gate: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  organization_id: string;
  organization_name: string | null;
  organization_code: string | null;
  departure_city_id: string;
  departure_city_name: string | null;
  departure_city_code: string | null;
  arrival_city_id: string;
  arrival_city_name: string | null;
  arrival_city_code: string | null;
  aircraft_type_id: string | null;
  aircraft_type_name: string | null;
  aircraft_type_code: string | null;
  source: string;
}

/**
 * Reshapes a flat unified_flights row into the nested Flight type
 * expected by all UI components.
 */
function reshapeToFlight(row: UnifiedFlightRow): Flight {
  return {
    id: row.id,
    flight_number: row.flight_number,
    scheduled_departure: row.scheduled_departure,
    scheduled_arrival: row.scheduled_arrival,
    status: row.status as Flight['status'],
    status_reason: row.status_reason ?? undefined,
    gate: row.gate ?? undefined,
    created_at: row.created_at,
    updated_at: row.updated_at,
    created_by: row.created_by ?? '',
    updated_by: row.updated_by ?? '',
    organization_id: row.organization_id,
    departure_city_id: row.departure_city_id,
    arrival_city_id: row.arrival_city_id,
    aircraft_type_id: row.aircraft_type_id ?? '',
    organization: row.organization_id
      ? {
          id: row.organization_id,
          name: row.organization_name ?? '',
          code: row.organization_code ?? '',
        }
      : undefined,
    departure_city: row.departure_city_id
      ? {
          id: row.departure_city_id,
          name: row.departure_city_name ?? '',
          code: row.departure_city_code ?? '',
        }
      : undefined,
    arrival_city: row.arrival_city_id
      ? {
          id: row.arrival_city_id,
          name: row.arrival_city_name ?? '',
          code: row.arrival_city_code ?? '',
        }
      : undefined,
    aircraft_type: row.aircraft_type_id
      ? {
          id: row.aircraft_type_id,
          name: row.aircraft_type_name ?? '',
          code: row.aircraft_type_code ?? '',
        }
      : undefined,
  };
}

/**
 * Fetches flights from the unified_flights view which merges
 * the flights table (live data) with flight_daily_cache (scheduled patterns).
 * Returns data shaped as Flight[] for direct use by UI components.
 */
export async function fetchUnifiedFlights(options?: {
  gteDate?: string;
  ltDate?: string;
}): Promise<Flight[]> {
  let query = supabase
    .from('unified_flights' as any)
    .select('*');

  if (options?.gteDate) {
    query = query.gte('scheduled_departure', options.gteDate);
  }
  if (options?.ltDate) {
    query = query.lt('scheduled_departure', options.ltDate);
  }

  query = query.order('scheduled_departure', { ascending: true });

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching unified flights:', error);
    throw error;
  }

  const rows = (data ?? []) as unknown as UnifiedFlightRow[];
  return rows.map(reshapeToFlight);
}
